import { useEffect, useRef, useState } from 'react'
import type { PropsWithChildren } from 'react'
import Image from '~/components/image/Image'
import { IKYU_RESTAURANT_NO_IMAGE_URL, PPG_DEFAULT_IMAGE_URL } from '~/lib/constants/constants'
import { makeUrl } from '~/lib/image/image'
import { cx, sva } from '~/styled-system/css'
import { DotsPagination, FractionPagination } from './pagination'

type ImageElement = {
  url: string
  name: string
}

const ikyuStyles = sva({
  slots: ['container', 'images', 'image'],
  base: {
    container: {
      position: 'relative',
    },
    images: {
      display: 'flex',
      overflowX: 'auto',
      scrollbar: 'hidden',
      scrollSnapType: '[x mandatory]',
    },
    image: {
      height: 'auto',
      flex: 'none',
      scrollSnapAlign: 'start',
      scrollSnapStop: 'always',
      width: '[!100%]',
    },
  },
  variants: {
    rounded: {
      true: {
        images: { borderRadius: '[6px 6px 0 0]' },
      },
      false: {},
    },
  },
})

const ppgStyles = sva({
  slots: ['container', 'images', 'image'],
  base: {
    container: {
      position: 'relative',
    },
    images: {
      display: 'flex',
      overflowX: 'auto',
      scrollbar: 'hidden',
      scrollSnapType: '[x mandatory]',
      gap: '3',
    },
    image: {
      height: 'auto',
      flex: 'none',
      scrollSnapAlign: 'start',
      scrollSnapStop: 'always',
      borderRadius: 'md',
      width: '[300px]',
    },
  },
})

const styles = import.meta.env.VITE_MODE === 'ppg' ? ppgStyles : ikyuStyles

const NO_IMAGE_URL =
  import.meta.env.VITE_MODE === 'ppg' ? PPG_DEFAULT_IMAGE_URL : IKYU_RESTAURANT_NO_IMAGE_URL

export function Images({
  images,
  width,
  height,
  rounded = false,
  dot = 'static',
  fraction = false,
  children,
}: PropsWithChildren<{
  images: ImageElement[]
  width: number
  height: number
  rounded?: boolean
  dot?: 'none' | 'static' | 'dynamic'
  fraction?: boolean
}>) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const classes = styles({ rounded })

  useEffect(() => {
    const div = ref.current
    if (div === null) {
      return
    }

    function loadImage(div: HTMLDivElement, index: number) {
      const next = div.querySelector(`[data-intersection-index="${index}"]`)
      if (next !== null) {
        const src = next.getAttribute('data-src')
        if (src !== null && src !== next.getAttribute('src')) {
          next.setAttribute('src', src)
        }
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLImageElement
            const index = parseInt(target.dataset.intersectionIndex ?? '0')
            setCurrentIndex(index)
            // 画像がスワイプで表示されたら、ひとつ右とその次の画像をロードする
            // PPG では次の画像が少しだけ見えているし、一休でも余裕をもって取得した方がスワイプ体験がよい
            loadImage(div, index + 1)
            loadImage(div, index + 2)
          }
        }
      },
      { root: div, threshold: 0.5 }
    )
    for (const image of images) {
      const e = div.querySelector(`[data-intersection-index="${images.indexOf(image)}"]`)
      if (e === null) {
        continue
      }
      observer.observe(e)
    }

    return () => {
      observer.disconnect()
    }
  }, [images])

  return (
    <div className={classes.container}>
      <div className={classes.images} ref={ref}>
        {images.map((image, i) => (
          <Image
            key={`${image.url}-${i}`}
            // リスト表示するだけで大量の画像がロードされるのを防ぐために、
            // 3枚目以降はスワイプされるまでは画像を読み込まない
            src={i <= 1 ? image.url : NO_IMAGE_URL}
            // IntersectionObserver でスワイプ時に遅延ロードさせる画像 URL
            // DeferredImage と同じ imgix の URL を makeUrl で生成している
            // makeUrl は二重に変換しないので DeferredImage に渡す前に使っても大丈夫
            data-src={makeUrl(image.url, width, height)}
            alt={`${image.name}-${i}`}
            data-intersection-index={i}
            // FIXME Image コンポーネントで w-full という classname に依存しているので cx で指定
            className={cx(classes.image, 'w-full')}
            width={width}
            height={height}
            usePlaceholder={true}
          />
        ))}
      </div>
      {dot !== 'none' && (
        <DotsPagination length={images.length} index={currentIndex} dynamic={dot === 'dynamic'} />
      )}
      {fraction && <FractionPagination length={images.length} index={currentIndex} />}
      {children}
    </div>
  )
}
